// ── V170: 투자비 · 감가상각 · 투자 회수 · 누적 순이익률 (손익 화면) ─────────────────────
// 계산은 utils/investment.js. 저장은 profitLoss._investments (onChange로 전체 객체)
import React, { useState } from 'react';
import { L, M, ym } from '../i18n/L';
import { trLang } from '../i18n/L';
import {
  ASSET_TYPES, ASSET_BY_ID, LIFE_OPTIONS, depOfItem, bookValue, investTotals, isDeposit,
} from '../utils/investment';

const thisMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const newId = () => 'iv' + Date.now().toString(36) + Math.random().toString(36).slice(2, 5);
const lifeLabel = (m) => (m % 12 === 0 ? L("{0}년", m / 12) : L("{0}개월", m));

const inp = { padding: '7px 8px', border: '1.5px solid #e5e7eb', borderRadius: 7, fontSize: 13, background: '#fff', minWidth: 0 };

export default function InvestmentPanel({ inv, month, depThisMonth, pay, onChange, money = M }) {
  const zh = trLang() === 'zh';
  const items = inv.items || [];
  const [open, setOpen] = useState(items.length === 0);
  const [guide, setGuide] = useState(false);
  const [draft, setDraft] = useState(null);
  const tot = investTotals(items);

  const save = (nextItems, patch = {}) => onChange({ ...inv, ...patch, items: nextItems });
  const setItem = (id, patch) => save(items.map(it => (it.id === id ? { ...it, ...patch } : it)));
  const addDraft = () => setDraft({ type: 'interior', name: '', amount: '', start: month || thisMonth(), months: 60 });
  const commitDraft = () => {
    const amount = Number(draft.amount) || 0;
    if (!(amount > 0)) return;
    const t = ASSET_BY_ID[draft.type] || ASSET_TYPES[0];
    save([...items, { id: newId(), type: t.id, name: String(draft.name || '').trim() || (zh ? t.zh : t.ko), amount, start: draft.start || month, months: t.kind === 'deposit' ? 0 : Number(draft.months) || t.months }]);
    setDraft(null);
  };

  const rate = pay.rate == null ? 0 : Math.max(0, Math.min(100, pay.rate));
  return (
    <div data-pl-invest>
      {/* 요약 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 10 }}>
        <Stat label={L("총 투자비")} value={money(tot.total)} sub={tot.deposit > 0 ? L("보증금 {0} 포함", money(tot.deposit)) : ''} />
        <Stat label={L("이달 감가상각비")} value={money(depThisMonth)} sub={L("비용에 자동 반영")} data="dep" />
        <Stat label={L("누적 순이익률")} value={pay.cumMargin == null ? '—' : `${pay.cumMargin}%`}
          sub={pay.months ? L("{0}개월 · 누적 순이익 {1}", pay.months, money(pay.cumProfit)) : ''} data="cum" color={pay.cumMargin != null && pay.cumMargin < 0 ? '#DC2626' : undefined} />
        <Stat label={L("투자 회수율")} value={pay.rate == null ? '—' : `${pay.rate}%`}
          sub={pay.target > 0 ? L("{0} / {1}", money(Math.max(0, pay.cumCash)), money(pay.target)) : ''} data="rate" />
      </div>
      {pay.target > 0 && (
        <div style={{ marginBottom: 10 }} data-pl-payback>
          <div style={{ height: 10, background: '#F4F4F5', borderRadius: 999, overflow: 'hidden' }}>
            <div style={{ width: `${rate}%`, height: '100%', background: rate >= 100 ? '#15803D' : '#FF6F0F' }} />
          </div>
          <div style={{ fontSize: 12, color: '#3f3f46', marginTop: 6, lineHeight: 1.6 }}>
            {pay.recoveredAt
              ? <b style={{ color: '#15803D' }}>✅ {L("{0}에 투자비를 다 회수했어요", ym(pay.recoveredAt))}</b>
              : pay.expected
                ? <span>📅 {L("예상 회수 시점")} <b>{ym(pay.expected)}</b> {L("(앞으로 약 {0}개월 · 최근 월평균 영업현금 {1} 기준)", pay.monthsLeft, money(pay.avgCash))}</span>
                : <span style={{ color: '#B45309' }}>⚠️ {pay.avgCash <= 0 && pay.months > 0 ? L("최근 영업현금이 마이너스라 회수 시점을 계산할 수 없어요") : L("매출·비용이 쌓이면 회수 시점을 계산해요")}</span>}
            <div style={{ color: '#9ca3af', fontSize: 11 }}>{L("회수액 = 순이익 + 감가상각비(현금이 나가지 않는 비용) · 보증금은 돌려받는 돈이라 회수 대상에서 뺐어요")}</div>
          </div>
        </div>
      )}

      {/* 회계 처리 안내 */}
      <button type="button" data-pl-invest-guide onClick={() => setGuide(g => !g)}
        style={{ border: 'none', background: 'none', color: '#1A56A0', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: '2px 0', marginBottom: 6 }}>
        📘 {L("회계에서는 이렇게 처리해요")} {guide ? '▲' : '▼'}
      </button>
      {guide && (
        <div data-pl-invest-guide-box style={{ background: '#F8FAFC', borderRadius: 10, padding: '10px 12px', fontSize: 12, color: '#3f3f46', lineHeight: 1.7, marginBottom: 10 }}>
          <div>• <b>{L("자산으로 두고 나눠서 비용 처리")}</b> — {L("인테리어·설비·집기처럼 여러 해 쓰는 것은 산 달에 한 번에 비용으로 잡지 않고 '유형자산'으로 두고, 쓰는 기간(내용연수)에 나눠 매달 '감가상각비'로 비용 처리해요.")}</div>
          <div>• <b>{L("정액법")}</b> — {L("매달 같은 금액 = 투자비 ÷ 개월 수 (남는 가치 0, 산 달부터). 남은 금액은 '장부가액'이에요.")}</div>
          <div>• <b>{L("권리금")}</b> — {L("'무형자산'이라 같은 방식으로 나눠 상각해요.")}</div>
          <div>• <b>{L("보증금")}</b> — {L("돌려받는 돈이라 비용이 아니에요(자산). 상각하지 않아요.")}</div>
          <div>• <b>{L("투자 회수")}</b> — {L("감가상각비는 현금이 나가지 않는 비용이라, 회수액은 순이익 + 감가상각비(영업현금)로 계산해요.")}</div>
          <div>• <b>{L("기간 고르기")}</b> — {L("보통 인테리어 = 임대 계약 기간 또는 5년, 주방 설비 5년, 가구·집기 3~5년, 전자기기·POS 3년.")}</div>
          <div style={{ color: '#6b7280' }}>• {L("세법 최소 기간 참고 — 중국: 전자기기 3년 · 가구·기구 5년 · 기계설비 10년 · 차량 4년, 임차 매장 인테리어는 남은 임대 기간에 나눠 상각 / 한국: 차량·공구·기구·비품 5년.")}</div>
          <div style={{ color: '#9ca3af' }}>{L("※ 셈핏 손익은 경영 판단용이에요. 세금 신고는 세무사(会计)와 맞춰 주세요. 거래장부에 이미 같은 투자비를 비용으로 넣었다면 두 번 잡히니 한쪽만 두세요.")}</div>
        </div>
      )}

      {/* 목록 */}
      <button type="button" onClick={() => setOpen(o => !o)} data-pl-invest-toggle
        style={{ border: 'none', background: 'none', color: '#3f3f46', fontSize: 13, fontWeight: 800, cursor: 'pointer', padding: '4px 0' }}>
        {L("투자 항목 {0}개", items.length)} {open ? '▲' : '▼'}
      </button>
      {open && (
        <div style={{ display: 'grid', gap: 6, marginTop: 6 }}>
          {items.map(it => {
            const t = ASSET_BY_ID[it.type] || ASSET_TYPES[0];
            const bv = bookValue(it, month);
            const dep = depOfItem(it, month);
            return (
              <div key={it.id} data-pl-invest-item={it.id} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 10, background: '#fff' }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <select value={it.type} onChange={e => { const nt = ASSET_BY_ID[e.target.value]; setItem(it.id, { type: nt.id, months: nt.kind === 'deposit' ? 0 : (Number(it.months) || nt.months) }); }} style={{ ...inp, flex: '0 0 auto' }}>
                    {ASSET_TYPES.map(a => <option key={a.id} value={a.id}>{zh ? a.zh : a.ko}</option>)}
                  </select>
                  <input value={it.name || ''} onChange={e => setItem(it.id, { name: e.target.value })} placeholder={L("항목 이름")} style={{ ...inp, flex: '1 1 120px' }} />
                  <input type="number" min="0" value={it.amount || ''} onChange={e => setItem(it.id, { amount: Number(e.target.value) || 0 })} style={{ ...inp, width: 110, textAlign: 'right' }} />
                  <input type="month" value={it.start || ''} onChange={e => setItem(it.id, { start: e.target.value })} style={{ ...inp, width: 140 }} title={L("투자한 달 (상각 시작)")} />
                  {t.kind !== 'deposit' && (
                    <select value={it.months} onChange={e => setItem(it.id, { months: Number(e.target.value) })} style={inp} title={L("감가상각 기간(내용연수)")} data-pl-invest-life>
                      {[...new Set([...LIFE_OPTIONS, Number(it.months) || 60])].sort((a, b) => a - b).map(m => <option key={m} value={m}>{lifeLabel(m)}</option>)}
                    </select>
                  )}
                  <button type="button" onClick={() => { if (window.confirm(L("이 투자 항목을 지울까요?"))) save(items.filter(x => x.id !== it.id)); }}
                    style={{ border: 'none', background: 'none', color: '#DC2626', fontSize: 13, cursor: 'pointer' }}>🗑</button>
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>
                  {isDeposit(it)
                    ? L("보증금 — 비용 아님 · 돌려받는 자산")
                    : L("월 {0} · {1} · 상각 누계 {2} · 장부가액 {3}{4}", money(dep), t.kind === 'intangible' ? L("무형자산 상각") : L("정액법"), money(bv.acc), money(bv.book), bv.endMonth ? L(" · {0}까지", ym(bv.endMonth)) : '')}
                </div>
              </div>
            );
          })}
          {draft ? (
            <div data-pl-invest-new style={{ border: '1.5px dashed #FF6F0F', borderRadius: 10, padding: 10, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', background: '#FFFBF5' }}>
              <select data-pl-invest-new-type value={draft.type} onChange={e => { const nt = ASSET_BY_ID[e.target.value]; setDraft({ ...draft, type: nt.id, months: nt.months || draft.months }); }} style={inp}>
                {ASSET_TYPES.map(a => <option key={a.id} value={a.id}>{zh ? a.zh : a.ko}</option>)}
              </select>
              <input data-pl-invest-new-name value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} placeholder={L("항목 이름 (예: 홀 인테리어)")} style={{ ...inp, flex: '1 1 140px' }} />
              <input data-pl-invest-new-amount type="number" min="0" value={draft.amount} onChange={e => setDraft({ ...draft, amount: e.target.value })} placeholder={L("금액")} style={{ ...inp, width: 110, textAlign: 'right' }} />
              <input data-pl-invest-new-start type="month" value={draft.start} onChange={e => setDraft({ ...draft, start: e.target.value })} style={{ ...inp, width: 140 }} />
              {(ASSET_BY_ID[draft.type] || {}).kind !== 'deposit' && (
                <select data-pl-invest-new-life value={draft.months} onChange={e => setDraft({ ...draft, months: Number(e.target.value) })} style={inp}>
                  {LIFE_OPTIONS.map(m => <option key={m} value={m}>{L("감가상각 {0}", lifeLabel(m))}</option>)}
                </select>
              )}
              <button type="button" data-pl-invest-new-save onClick={commitDraft} disabled={!(Number(draft.amount) > 0)}
                style={{ padding: '7px 14px', borderRadius: 7, border: 'none', background: Number(draft.amount) > 0 ? '#FF6F0F' : '#d1d5db', color: '#fff', fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>{L("추가")}</button>
              <button type="button" onClick={() => setDraft(null)} style={{ border: 'none', background: 'none', color: '#6b7280', fontSize: 12, cursor: 'pointer' }}>{L("취소")}</button>
              {draft.amount > 0 && (ASSET_BY_ID[draft.type] || {}).kind !== 'deposit' && (
                <div style={{ flexBasis: '100%', fontSize: 11, color: '#9A3412' }}>{L("매달 {0}씩 {1} 동안 비용으로 잡혀요", money(Math.floor((Number(draft.amount) / draft.months) * 100) / 100), lifeLabel(draft.months))}</div>
              )}
            </div>
          ) : (
            <button type="button" data-pl-invest-add onClick={addDraft}
              style={{ padding: '9px 12px', borderRadius: 9, border: '1.5px dashed #FF6F0F', background: '#fff', color: '#C2410C', fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>＋ {L("투자비 추가")}</button>
          )}
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: '#6b7280', marginTop: 4, flexWrap: 'wrap' }}>
            {L("누적 계산 시작 월 (오픈 월)")}
            <input type="month" data-pl-invest-open value={inv.openMonth || ''} onChange={e => onChange({ ...inv, openMonth: e.target.value })} style={{ ...inp, width: 150 }} />
            <span style={{ color: '#9ca3af' }}>{L("비우면 가장 이른 투자 월")}</span>
          </label>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub, data, color }) {
  return (
    <div data-pl-invest-stat={data || label} style={{ background: '#FAFAFA', borderRadius: 10, padding: '9px 10px' }}>
      <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 900, color: color || '#18181B' }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
